import { useState } from 'react';
import { useAssistantStore } from '@/stores/useAssistantStore';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, Lock, VolumeX, Power, Layers, CircleDot } from 'lucide-react';
import type { VSTACKRecipe } from '@/types/assistant';

const DEMO_TIMESTAMP = Date.UTC(2026, 6, 14);

const DEMO_RECIPES: VSTACKRecipe[] = [
  {
    id: 'vstack-master',
    name: 'Master Bus Chain',
    version: '1.0.0',
    createdAt: DEMO_TIMESTAMP,
    modifiedAt: DEMO_TIMESTAMP,
    inputRouting: 'default',
    outputRouting: 'master',
    parentRecipeId: null,
    exportFormat: 'json',
    layers: [
      { id: 'l1', moduleRef: 'eq-linear', humanLabel: 'High-Pass Filter', developerLabel: 'HPF @ 30Hz', parameters: { freq: 30, q: 0.7 }, enabled: true, muted: false, locked: false, order: 0 },
      { id: 'l2', moduleRef: 'comp-vca', humanLabel: 'Glue Compressor', developerLabel: 'VCA Comp 4:1', parameters: { threshold: -18, ratio: 4 }, enabled: true, muted: false, locked: false, order: 1 },
      { id: 'l3', moduleRef: 'eq-param', humanLabel: 'Sweetener EQ', developerLabel: 'Pultec EQ', parameters: { boost: 2, freq: 12000 }, enabled: true, muted: false, locked: false, order: 2 },
      { id: 'l4', moduleRef: 'limiter', humanLabel: 'True Peak Limiter', developerLabel: 'TP Limiter -1dB', parameters: { ceiling: -1 }, enabled: true, muted: false, locked: true, order: 3 },
    ]
  },
  {
    id: 'vstack-vocal',
    name: 'Vocal Chain',
    version: '1.0.0',
    createdAt: DEMO_TIMESTAMP,
    modifiedAt: DEMO_TIMESTAMP,
    inputRouting: 'mic-1',
    outputRouting: 'vocal-bus',
    parentRecipeId: null,
    exportFormat: 'json',
    layers: [
      { id: 'v1', moduleRef: 'gate', humanLabel: 'Noise Gate', developerLabel: 'Gate -60dB', parameters: { threshold: -60 }, enabled: true, muted: false, locked: false, order: 0 },
      { id: 'v2', moduleRef: 'de-esser', humanLabel: 'De-Esser', developerLabel: 'De-Ess 6kHz', parameters: { freq: 6000, amount: 4 }, enabled: true, muted: false, locked: false, order: 1 },
      { id: 'v3', moduleRef: 'comp-optical', humanLabel: 'Optical Compressor', developerLabel: 'LA-2A Style', parameters: { gain: 6 }, enabled: true, muted: false, locked: false, order: 2 },
      { id: 'v4', moduleRef: 'eq-param', humanLabel: 'Air Band', developerLabel: 'Air +4dB @ 16k', parameters: { freq: 16000, gain: 4 }, enabled: true, muted: false, locked: false, order: 3 },
      { id: 'v5', moduleRef: 'reverb-plate', humanLabel: 'Plate Reverb', developerLabel: 'Plate 2.4s', parameters: { decay: 2.4, mix: 20 }, enabled: true, muted: false, locked: false, order: 4 },
    ]
  },
  {
    id: 'vstack-drum',
    name: 'Drum Bus',
    version: '1.0.0',
    createdAt: DEMO_TIMESTAMP,
    modifiedAt: DEMO_TIMESTAMP,
    inputRouting: 'drum-group',
    outputRouting: 'master',
    parentRecipeId: null,
    exportFormat: 'json',
    layers: [
      { id: 'd1', moduleRef: 'transient', humanLabel: 'Transient Shaper', developerLabel: 'Attack +6', parameters: { attack: 6, sustain: -3 }, enabled: true, muted: false, locked: false, order: 0 },
      { id: 'd2', moduleRef: 'comp-fet', humanLabel: 'FET Compressor', developerLabel: '1176 All-Buttons', parameters: { ratio: 20, attack: 2 }, enabled: true, muted: false, locked: false, order: 1 },
      { id: 'd3', moduleRef: 'saturation', humanLabel: 'Tape Saturation', developerLabel: 'Tape 15ips', parameters: { drive: 3.5 }, enabled: true, muted: false, locked: false, order: 2 },
    ]
  }
];

export function VSTACKHologram() {
  const { vstackPlanes } = useAssistantStore();
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>('vstack-master');
  const recipes = vstackPlanes.length > 0
    ? vstackPlanes.flatMap(p => p.engines.map(e => ({
        id: e.id,
        name: e.name,
        version: '1.0.0',
        createdAt: DEMO_TIMESTAMP,
        modifiedAt: DEMO_TIMESTAMP,
        layers: [],
        inputRouting: 'default',
        outputRouting: 'default',
        parentRecipeId: null,
        exportFormat: 'json'
      })))
    : DEMO_RECIPES;

  return (
    <div className="flex flex-col h-full bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-surface/30">
        <Layers size={14} className="text-violet" />
        <span className="text-label text-violet">VSTACK HOLOGRAPHIC LIST</span>
        <span className="ml-auto text-mono-sm text-tertiary">{recipes.length} RECIPES</span>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {recipes.map((recipe) => (
          <div key={recipe.id} className="vstack-deck">
            <button
              onClick={() => setExpandedRecipe(expandedRecipe === recipe.id ? null : recipe.id)}
              className="flex items-center justify-between w-full px-3 py-2 hover:bg-hover/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                {expandedRecipe === recipe.id ? (
                  <ChevronDown size={14} className="text-tertiary" />
                ) : (
                  <ChevronRight size={14} className="text-tertiary" />
                )}
                <span className="text-heading-sm text-primary">{recipe.name}</span>
                <span className="text-mono-sm text-tertiary">v{recipe.version}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-label text-tertiary">{recipe.layers.length} LAYERS</span>
                <CircleDot size={10} className="text-neon" />
              </div>
            </button>

            <AnimatePresence>
              {expandedRecipe === recipe.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-2">
                    {recipe.layers.length === 0 && (
                      <p className="text-body-sm text-tertiary italic py-2">Empty recipe — add layers</p>
                    )}
                    {recipe.layers.map((layer, index) => (
                      <motion.div
                        key={layer.id}
                        initial={{ x: -10, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className={`flex items-center gap-2 py-1.5 border-b border-border-subtle/50 last:border-0 ${
                          !layer.enabled ? 'opacity-40' : ''
                        }`}
                      >
                        <span className="text-mono-sm text-tertiary w-5">{layer.order + 1}</span>
                        <Power size={10} className={layer.enabled ? 'text-neon' : 'text-disabled'} />
                        {layer.muted && <VolumeX size={10} className="text-fire" />}
                        {layer.locked && <Lock size={10} className="text-gold" />}
                        <span className="text-mono-md text-primary flex-1 truncate">{layer.humanLabel}</span>
                        <span className="text-mono-sm text-tertiary">{layer.moduleRef}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}
